import React, { useEffect, useState } from "react";
import TopBar from "../components/TopBar";
import { api, fmtUSD, fmtNum, EXPENSE_CATEGORIES, API } from "../lib/api";
import { downloadCsv } from "../lib/csv";
import { useYear } from "../context/YearContext";
import { Printer, FileText, Paperclip, Download, Package } from "lucide-react";

export default function Reports() {
  const { year } = useYear();
  const [data, setData] = useState(null);
  const [customCats, setCustomCats] = useState([]);

  useEffect(() => {
    api.get(`/reports/schedule-f?year=${year}`).then((r) => setData(r.data));
    api.get(`/options/expense_category`).then((r) => setCustomCats(r.data)).catch(() => {});
  }, [year]);

  const handlePrint = () => window.print();
  const handleBundle = () => {
    // Hit the backend ZIP endpoint directly so the browser triggers a file save.
    window.location.assign(`${API}/reports/tax-bundle?year=${year}`);
  };

  if (!data) {
    return (
      <div>
        <TopBar title="Tax Reports" subtitle="Loading…" />
      </div>
    );
  }

  const { summary, yields, expenses, mileage, fields, equipment = [], internal_services = null } = data;
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  // Combined label map: built-in EXPENSE_CATEGORIES + user-added customs
  const labelByValue = new Map();
  EXPENSE_CATEGORIES.forEach((c) => labelByValue.set(c.value, c.label));
  customCats.forEach((c) => { if (!labelByValue.has(c.value)) labelByValue.set(c.value, c.label); });
  const catLabel = (v) => labelByValue.get(v) || v;

  // All categories present this year — preserves built-in order, then appends customs
  const allCategoryValues = [
    ...EXPENSE_CATEGORIES.map((c) => c.value),
    ...Object.keys(summary.expenses_by_category).filter((v) => !EXPENSE_CATEGORIES.some((c) => c.value === v)),
  ];

  // Group expenses by category for cleaner printed ledger
  const expensesByCategory = allCategoryValues
    .map((v) => ({ value: v, label: catLabel(v), rows: expenses.filter((e) => e.category === v) }))
    .filter((g) => g.rows.length > 0);

  const allReceipts = [
    ...yields.flatMap((y) => (y.receipts || []).map((r) => ({ ...r, source: `Yield · ${y.field_name || y.crop} · ${y.date}` }))),
    ...expenses.flatMap((e) => (e.receipts || []).map((r) => ({ ...r, source: `Expense · ${catLabel(e.category)} · ${e.date}` }))),
    ...equipment.flatMap((eq) => (eq.receipts || []).map((r) => ({ ...r, source: `Equipment · ${eq.name} · ${eq.purchase_date}` }))),
  ];

  return (
    <div>
      <TopBar
        title="Tax-Ready Report"
        subtitle={`Schedule F style summary for ${year}. Print it, or grab the full ZIP bundle for your accountant.`}
        actions={
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <button
              data-testid="export-report-csv"
              onClick={() => {
                const combined = [
                  ...yields.map((y) => ({ section: "income", date: y.date, field: y.field_name, crop: y.crop, description: `${fmtNum(y.quantity)} ${y.unit} @ ${fmtUSD(y.price_per_unit)}`, category: "yield", amount: y.total_revenue })),
                  ...expenses.map((e) => ({ section: "expense", date: e.date, field: "", crop: "", description: `${e.vendor || ""} ${e.description || ""}`.trim(), category: catLabel(e.category), amount: -(e.amount) })),
                  ...mileage.map((m) => ({ section: "mileage", date: m.date, field: "", crop: "", description: `${m.vehicle} · ${m.purpose} · ${fmtNum(m.miles, 1)}mi × ${m.business_pct}%`, category: "mileage", amount: -(m.miles * m.business_pct / 100 * summary.mileage_rate).toFixed(2) })),
                ];
                downloadCsv(`schedule-f-${year}.csv`, combined);
              }}
              className="border border-[#1F1E1B] px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              data-testid="download-tax-bundle"
              onClick={handleBundle}
              className="border border-[#1F1E1B] px-4 py-2 text-sm font-bold uppercase tracking-wider bg-[#1A4135] text-white hover:bg-[#225748] transition-colors flex items-center gap-2"
              title="Download a ZIP with all CSVs and receipt photos"
            >
              <Package className="w-4 h-4" /> Tax Bundle (ZIP)
            </button>
            <button
              data-testid="print-report-btn"
              onClick={handlePrint}
              className="bg-[#1F1E1B] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#2A2924] transition-colors flex items-center gap-2"
            >
              <Printer className="w-4 h-4" /> Print Report
            </button>
          </div>
        }
      />

      <div className="print-container bg-white border-2 border-[#1F1E1B] p-8 md:p-12" data-testid="tax-report">
        {/* Report header */}
        <div className="flex items-start justify-between border-b-2 border-[#1F1E1B] pb-5 mb-8 print-avoid">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-[#66645E]">
              <FileText className="w-3 h-3" />
              AcreLatch · Tax Year {year}
            </div>
            <h1 className="font-display text-4xl font-black tracking-tight mt-2">
              Farm Profit &amp; Loss Statement
            </h1>
            <div className="text-sm text-[#66645E] mt-2">
              Schedule F reference · Generated {today}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Net Farm Income</div>
            <div className={`font-display text-3xl font-black tabular ${summary.net_income >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`} data-testid="net-income-value">
              {fmtUSD(summary.net_income)}
            </div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 print-avoid">
          <div className="border border-[#1F1E1B] p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Part I — Gross Income</div>
            <div className="font-display text-2xl font-black tabular mt-1">{fmtUSD(summary.total_revenue)}</div>
          </div>
          <div className="border border-[#1F1E1B] p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Part II — Expenses</div>
            <div className="font-display text-2xl font-black tabular mt-1">{fmtUSD(summary.total_expenses)}</div>
          </div>
          <div className="border border-[#1F1E1B] p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Mileage Deduction</div>
            <div className="font-display text-2xl font-black tabular mt-1">{fmtUSD(summary.mileage_deduction)}</div>
            <div className="text-[10px] text-[#66645E] mt-1">{fmtNum(summary.total_miles_business, 1)} mi × ${summary.mileage_rate}</div>
          </div>
          <div className="border border-[#1F1E1B] p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Depreciation</div>
            <div className="font-display text-2xl font-black tabular mt-1">{fmtUSD(summary.depreciation || 0)}</div>
            <div className="text-[10px] text-[#66645E] mt-1">{(summary.depreciation_detail || []).length} item{(summary.depreciation_detail || []).length === 1 ? "" : "s"}</div>
          </div>
        </div>

        {/* Cost of services performed in-house (informational callout) */}
        {internal_services && internal_services.combined_total > 0 && (
          <div className="border-2 border-[#1F1E1B] bg-[#1F1E1B] text-white p-5 mb-10 print:bg-white print:text-black print:border-black" data-testid="schedule-f-internal-services">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70 print:opacity-100">
                  Informational — not part of Schedule F
                </div>
                <div className="font-display text-xl font-black mt-1">
                  Cost of services performed in-house for the Farm
                </div>
                <div className="text-[11px] opacity-70 print:opacity-100 mt-1">
                  Value of Repair + Trucking work the Farm got done by J&amp;K Repair and J&amp;K Trucking in {year}.
                  Already reflected in Farm expenses (categories <em>internal-repair</em> + <em>internal-trucking</em>).
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.18em] opacity-70 print:opacity-100">Combined Value</div>
                <div className="font-display text-3xl font-black tabular text-[#E5A835] print:text-black" data-testid="schedule-f-internal-total">
                  {fmtUSD(internal_services.combined_total)}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="bg-white/10 print:bg-[#F5F4F0] border border-white/20 print:border-[#1F1E1B] p-3" data-testid="schedule-f-internal-repair">
                <div className="text-[10px] uppercase tracking-wider opacity-70 print:opacity-100">J&amp;K Repair</div>
                <div className="font-bold tabular text-lg">{fmtUSD(internal_services.repair.total)}</div>
                <div className="text-[11px] opacity-80 print:opacity-100">
                  {internal_services.repair.invoice_count} job{internal_services.repair.invoice_count === 1 ? "" : "s"} ·
                  {" "}{fmtNum(internal_services.repair.labor_hours, 1)} labor hrs ·
                  {" "}{fmtUSD(internal_services.repair.parts_cost)} parts
                </div>
              </div>
              <div className="bg-white/10 print:bg-[#F5F4F0] border border-white/20 print:border-[#1F1E1B] p-3" data-testid="schedule-f-internal-trucking">
                <div className="text-[10px] uppercase tracking-wider opacity-70 print:opacity-100">J&amp;K Trucking</div>
                <div className="font-bold tabular text-lg">{fmtUSD(internal_services.trucking.total)}</div>
                <div className="text-[11px] opacity-80 print:opacity-100">
                  {internal_services.trucking.invoice_count} haul{internal_services.trucking.invoice_count === 1 ? "" : "s"} ·
                  {" "}{fmtNum(internal_services.trucking.miles, 0)} miles
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fields */}
        <Section title="Fields Farmed" count={fields.length}>
          <table className="ledger-table">
            <thead>
              <tr><th>Field</th><th>Crop</th><th className="num">Acreage</th><th>Location</th></tr>
            </thead>
            <tbody>
              {fields.map((f) => (
                <tr key={f.id}><td>{f.name}</td><td>{f.crop_type}</td><td className="num">{fmtNum(f.acreage, 2)}</td><td className="text-[#66645E]">{f.location || "—"}</td></tr>
              ))}
              {!fields.length && <tr><td colSpan={4} className="text-center py-6 text-[#66645E]">No fields.</td></tr>}
            </tbody>
          </table>
        </Section>

        {/* Income */}
        <Section title="Part I — Gross Farm Income (Sales of Produce)" count={yields.length}>
          <table className="ledger-table">
            <thead>
              <tr>
                <th>Date</th><th>Field</th><th>Crop</th>
                <th className="num">Qty</th><th className="num">Price</th><th className="num">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {yields.map((y) => (
                <tr key={y.id}>
                  <td className="tabular">{y.date}</td><td>{y.field_name}</td><td>{y.crop}</td>
                  <td className="num">{fmtNum(y.quantity)} {y.unit}</td>
                  <td className="num">{fmtUSD(y.price_per_unit)}</td>
                  <td className="num font-bold">{fmtUSD(y.total_revenue)}</td>
                </tr>
              ))}
              {!yields.length && <tr><td colSpan={6} className="text-center py-6 text-[#66645E]">No harvest records.</td></tr>}
              <tr className="bg-[#F5F4F0]">
                <td colSpan={5} className="font-bold uppercase tracking-wider text-xs">Total Gross Income</td>
                <td className="num font-display text-lg font-black">{fmtUSD(summary.total_revenue)}</td>
              </tr>
            </tbody>
          </table>
        </Section>

        {/* Expenses by category — totals */}
        <Section title="Part II — Expenses by Category">
          <table className="ledger-table">
            <thead>
              <tr><th>Category</th><th className="num">Total</th></tr>
            </thead>
            <tbody>
              {allCategoryValues.map((value) => {
                const v = summary.expenses_by_category[value] || 0;
                if (!v) return null;
                return (
                  <tr key={value}><td>{catLabel(value)}</td><td className="num font-bold">{fmtUSD(v)}</td></tr>
                );
              })}
              <tr className="bg-[#F5F4F0]">
                <td className="font-bold uppercase tracking-wider text-xs">Total Expenses</td>
                <td className="num font-display text-lg font-black">{fmtUSD(summary.total_expenses)}</td>
              </tr>
            </tbody>
          </table>
        </Section>

        {/* Expense detail — grouped by category for readability */}
        <Section title="Expense Detail Ledger" count={expenses.length} testid="expense-detail-section">
          {expensesByCategory.map((g) => {
            const subtotal = g.rows.reduce((s, e) => s + (e.amount || 0), 0);
            return (
              <div key={g.value} className="mb-6 print-avoid">
                <div className="px-2 py-1 bg-[#F5F4F0] border border-[#D2CEC1] flex items-baseline justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em]">{g.label}</span>
                  <span className="text-[11px] font-bold tabular">{fmtUSD(subtotal)} · {g.rows.length} entries</span>
                </div>
                <table className="ledger-table">
                  <thead>
                    <tr><th>Date</th><th>Vendor</th><th>Description</th><th className="num">Amount</th></tr>
                  </thead>
                  <tbody>
                    {g.rows.map((e) => (
                      <tr key={e.id}>
                        <td className="tabular">{e.date}</td>
                        <td>{e.vendor || "—"}</td>
                        <td className="text-[#66645E]">{e.description || "—"}</td>
                        <td className="num font-bold">{fmtUSD(e.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
          {!expenses.length && <div className="text-center py-6 text-[#66645E]">No expenses.</div>}
        </Section>

        {/* Mileage */}
        <Section title={`Vehicle Mileage — Standard Rate $${summary.mileage_rate}/mile`} count={mileage.length}>
          <table className="ledger-table">
            <thead>
              <tr><th>Date</th><th>Vehicle</th><th>Purpose</th><th className="num">Miles</th><th className="num">Biz %</th><th className="num">Deductible</th></tr>
            </thead>
            <tbody>
              {mileage.map((m) => {
                const d = m.miles * (m.business_pct / 100);
                return (
                  <tr key={m.id}>
                    <td className="tabular">{m.date}</td>
                    <td>{m.vehicle}</td>
                    <td className="text-[#66645E]">{m.purpose}</td>
                    <td className="num">{fmtNum(m.miles, 1)}</td>
                    <td className="num">{m.business_pct}%</td>
                    <td className="num font-semibold">{fmtNum(d, 1)} mi</td>
                  </tr>
                );
              })}
              {!mileage.length && <tr><td colSpan={6} className="text-center py-6 text-[#66645E]">No mileage.</td></tr>}
              <tr className="bg-[#F5F4F0]">
                <td colSpan={5} className="font-bold uppercase tracking-wider text-xs">Total Deduction ({fmtNum(summary.total_miles_business, 1)} mi)</td>
                <td className="num font-display text-lg font-black">{fmtUSD(summary.mileage_deduction)}</td>
              </tr>
            </tbody>
          </table>
        </Section>

        {/* Depreciation detail */}
        {(summary.depreciation_detail || []).length > 0 && (
          <Section title="Depreciation Schedule (MACRS / Straight-Line)" count={summary.depreciation_detail.length} testid="depreciation-section">
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Equipment</th>
                  <th className="num">Purchase Yr</th>
                  <th className="num">Life (yrs)</th>
                  <th>Method</th>
                  <th className="num">This Year</th>
                </tr>
              </thead>
              <tbody>
                {summary.depreciation_detail.map((d) => (
                  <tr key={d.equipment_id}>
                    <td>{d.name}</td>
                    <td className="num">{d.purchase_year}</td>
                    <td className="num">{d.useful_life_years}</td>
                    <td className="uppercase text-[#66645E]">{(d.method || "").replace("_", " ")}</td>
                    <td className="num font-bold">{fmtUSD(d.annual_depreciation)}</td>
                  </tr>
                ))}
                <tr className="bg-[#F5F4F0]">
                  <td colSpan={4} className="font-bold uppercase tracking-wider text-xs">Total Depreciation</td>
                  <td className="num font-display text-lg font-black">{fmtUSD(summary.depreciation || 0)}</td>
                </tr>
              </tbody>
            </table>
          </Section>
        )}

        {/* Bottom line */}
        <div className="border-t-2 border-[#1F1E1B] pt-6 mt-10 print-avoid">
          <div className="grid grid-cols-2 gap-10">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Net Farm Profit / (Loss)</div>
              <div className="text-sm text-[#66645E] mt-1">Gross income less expenses less mileage deduction less depreciation.</div>
            </div>
            <div className="text-right">
              <div className={`font-display text-5xl font-black tabular ${summary.net_income >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
                {fmtUSD(summary.net_income)}
              </div>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[#D2CEC1] text-[10px] text-[#66645E] tracking-wider">
            This document is a record-keeping aid intended to support filing of IRS Schedule F. It is not tax advice.
            Review with your tax professional before filing.
          </div>
        </div>

        {/* Receipt photos appendix — printed on its own page */}
        {allReceipts.length > 0 && (
          <div className="mt-12 pt-8 border-t-2 border-[#1F1E1B] print-break" data-testid="receipts-appendix">
            <div className="flex items-baseline justify-between border-b border-[#1F1E1B] pb-2 mb-4">
              <h3 className="font-display text-lg font-bold uppercase tracking-wider flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Receipt Photo Appendix
              </h3>
              <div className="text-[10px] font-bold tracking-[0.18em] text-[#66645E]">{allReceipts.length} attachments</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {allReceipts.map((r) => (
                <div key={r.id} className="border border-[#1F1E1B] print-avoid" style={{ pageBreakInside: "avoid" }}>
                  <div className="aspect-[4/3] bg-[#F5F4F0] overflow-hidden">
                    <img src={`${API}/files/${r.id}`} alt={r.filename} className="w-full h-full object-contain" />
                  </div>
                  <div className="px-2 py-1.5 border-t border-[#1F1E1B] text-[10px] uppercase tracking-wider text-[#66645E]">
                    {r.source}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, count, children, testid }) {
  return (
    <div className="mb-10 print-avoid" data-testid={testid}>
      <div className="flex items-baseline justify-between border-b border-[#1F1E1B] pb-2 mb-3">
        <h3 className="font-display text-lg font-bold uppercase tracking-wider">{title}</h3>
        {count != null && (
          <div className="text-[10px] font-bold tracking-[0.18em] text-[#66645E]">{count} entries</div>
        )}
      </div>
      <div className="border border-[#D2CEC1] overflow-x-auto">{children}</div>
    </div>
  );
}
